import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Types
export interface TelegramStatus {
  isLinked: boolean;
  telegramUsername?: string;
  linkedAt?: string;
}

export interface TelegramLink {
  chatLink: string;
  linkingToken: string;
  instructions: string;
}

interface TelegramState {
  status: TelegramStatus | null;
  linkData: TelegramLink | null;
  loading: boolean;
  error: string | null;
}

const initialState: TelegramState = {
  status: null,
  linkData: null,
  loading: false,
  error: null,
};

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks
export const fetchTelegramStatus = createAsyncThunk(
  'telegram/fetchStatus',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/telegram/status`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch Telegram status'));
    }
  }
);

export const generateTelegramLink = createAsyncThunk(
  'telegram/generateLink',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/telegram/generate-link`, {}, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to generate Telegram link'));
    }
  }
);

export const unlinkTelegram = createAsyncThunk(
  'telegram/unlink',
  async (_, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/telegram/unlink`, {
        headers: getAuthHeaders(getState)
      });
      return null;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to unlink Telegram'));
    }
  }
);

// Slice
const telegramSlice = createSlice({
  name: 'telegram',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLinkData: (state) => {
      state.linkData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch status
      .addCase(fetchTelegramStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTelegramStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload;
      })
      .addCase(fetchTelegramStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Generate link
      .addCase(generateTelegramLink.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateTelegramLink.fulfilled, (state, action) => {
        state.loading = false;
        state.linkData = action.payload;
      })
      .addCase(generateTelegramLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Unlink
      .addCase(unlinkTelegram.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unlinkTelegram.fulfilled, (state) => {
        state.loading = false;
        state.status = { isLinked: false };
        state.linkData = null;
      })
      .addCase(unlinkTelegram.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearLinkData } = telegramSlice.actions;
export default telegramSlice.reducer; 